import React, { useState, useMemo } from 'react';
import Wrapper from '../../components/Wrapper';
import './ReportsHealthyStyles.scss';
import { useReportHealth } from '../../hooks/useReportHealth';
import { useParams } from 'react-router-dom';
import { useGetAgencyQuery } from '../../store/builder/inspections.api';
import CustomPaginate from '../../components/PaginateComponent/index.jsx';
import { useTranslation } from '../../hooks/useTranslation';

const ITEMS_PER_PAGE = 20;

const ReportsHealthy = () => {
    const {t} = useTranslation();
    const { id } = useParams();
    const [agencyFilter, setAgencyFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const { data: agencyData } = useGetAgencyQuery();
    
    // Get the agency ID from the selected agency title
    const selectedAgencyId = useMemo(() => {
        if (!agencyFilter || !agencyData) return null;
        const selectedAgency = agencyData.find(agency => agency.title === agencyFilter);
        return selectedAgency ? selectedAgency.id : null;
    }, [agencyFilter, agencyData]);

    // Pass selected agency_id, status, and pagination parameters to the hook
    const { data: reportHealth, isLoading, error } = useReportHealth(
        id, 
        selectedAgencyId, 
        statusFilter, 
        currentPage,
        ITEMS_PER_PAGE
    );

    // Agency data from API query


    // Get unique agencies and statuses for filter options
    const agencyOptions = useMemo(() => {
        if (!agencyData || !Array.isArray(agencyData)) return [];
        return agencyData.map(agency => ({
            id: agency.id,
            title: agency.title
        }));
    }, [agencyData]);

    // Headers for table
    const headers = [
        t('reportsHealthy.tableHeaders.agencyTitle'),
        t('reportsHealthy.tableHeaders.facilityName'),
        t('reportsHealthy.tableHeaders.reportType'),
        t('reportsHealthy.tableHeaders.month'),
        t('reportsHealthy.tableHeaders.year'),
        t('reportsHealthy.tableHeaders.status'),
        t('reportsHealthy.tableHeaders.updatedAt')
    ];

    // Status options with translations
    const statusOptions = [
        { value: 'all', label: t('reportsHealthy.statusOptions.all') },
        { value: 'completed', label: t('reportsHealthy.statusOptions.completed') },
        { value: 'failed', label: t('reportsHealthy.statusOptions.failed') }
    ];

    // Extract data and pagination info from response
    const reportData = useMemo(() => {
        if (!reportHealth) return { data: [], total: 0, pages: 0 };
        
        // Handle different response structures
        if (Array.isArray(reportHealth)) {
            return { data: reportHealth, total: reportHealth.length, pages: 1 };
        }
        
        // Handle paginated response structure
        return {
            data: reportHealth.data || reportHealth.items || [],
            total: reportHealth.total || reportHealth.total_count || 0,
            pages: reportHealth.pages || reportHealth.total_pages || 1
        };
    }, [reportHealth]);

    // Handle page change
    const handlePageClick = (event) => {
        setCurrentPage(event.selected + 1);
    };

    const handleAgencyChange = (e) => {
        setAgencyFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    // Reset to first page when filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [agencyFilter, statusFilter]);

    // Clear all filters
    const clearFilters = () => {
        setAgencyFilter('');
        setStatusFilter('');
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return t('reportsHealthy.notAvailable');
        try {
            return new Date(dateString).toLocaleDateString();
        } catch (error) {
            return dateString;
        }
    };

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'status-badge--completed';
            case 'failed':
                return 'status-badge--failed';
            default:
                return 'status-badge--default';
        }
    };

    // Show loading state
    if (isLoading) {
        return (
            <Wrapper>
                <div className="reports-healthy">
                    <div className="reports-healthy__container">
                        <div className="reports-healthy__loading">
                            <div className="loading-spinner"></div>
                            <p>{t('reportsHealthy.loading')}</p>
                        </div>
                    </div>
                </div>
            </Wrapper>
        );
    }

    // Show error state
    if (error) {
        return (
            <Wrapper>
                <div className="reports-healthy">
                    <div className="reports-healthy__container">
                        <div className="reports-healthy__error">
                            <h2>{t('reportsHealthy.errorTitle')}</h2>
                            <p>{error.message || t('reportsHealthy.errorMessage')}</p>
                            <button onClick={() => window.location.reload()}>{t('reportsHealthy.retry')}</button>
                        </div>
                    </div>
                </div>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <div className="reports-healthy">
                <div className="reports-healthy__container">
                    <div className="reports-healthy__header">
                        <h1 className="reports-healthy__title">{t('reportsHealthy.title')}</h1>
                        <div className="reports-healthy__filters">
                            <div className="filter-group">
                                <label className="filter-label">{t('reportsHealthy.filters.agency')}</label>
                                <div className="filter-input-wrapper">
                                    <select 
                                        className="filter-select"
                                        value={agencyFilter}
                                        onChange={handleAgencyChange}
                                    >
                                        <option value="">{t('reportsHealthy.filters.allAgencies')}</option>
                                        {agencyOptions.map((agency) => (
                                            <option key={agency.id} value={agency.title}>
                                                {agency.title}
                                            </option>
                                        ))}
                                    </select>
                                    {agencyFilter && (
                                        <button 
                                            className="filter-clear-btn"
                                            onClick={() => setAgencyFilter('')}
                                            title={t('reportsHealthy.filters.clearAgency')}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="filter-group">
                                <label className="filter-label">{t('reportsHealthy.filters.status')}</label>
                                <div className="filter-input-wrapper">
                                    <select 
                                        className="filter-select"
                                        value={statusFilter}
                                        onChange={handleStatusChange}
                                    >
                                        {statusOptions.map((status, index) => (
                                            <option key={index} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </select>
                                    {statusFilter && (
                                        <button 
                                            className="filter-clear-btn"
                                            onClick={() => setStatusFilter('')}
                                            title={t('reportsHealthy.filters.clearStatus')}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                            {(agencyFilter || statusFilter) && (
                                <div className="filter-group">
                                    <button 
                                        className="clear-all-filters-btn"
                                        onClick={clearFilters}
                                        title={t('reportsHealthy.filters.clearAllTitle')}
                                    >
                                        {t('reportsHealthy.filters.clearAll')}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="reports-healthy__count">
                            {reportData.total} {t('reportsHealthy.records')}
                        </div>
                    </div>

                    <div className="reports-healthy__table-container">
                        <div className="reports-healthy__table-wrapper">
                            <table className="reports-healthy__table">
                                <thead>
                                    <tr>
                                        {headers.map((header, index) => (
                                            <th key={index}>{header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.data.map((row, index) => (
                                        <tr key={index} className="reports-healthy__row">
                                            <td>
                                                <span className="agency-title">{row.agency_title}</span>
                                            </td>
                                            <td>
                                                <span className="facility-name">{row.facility_name}</span>
                                            </td>
                                            <td>
                                                <span className="facility-name">{row.type}</span>
                                            </td>
                                            <td>
                                                <span className="month">{row.month}</span>
                                            </td>
                                            <td>
                                                <span className="year">{row.year}</span>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${getStatusBadgeClass(row.status)}`}>
                                                    {row.status}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="date">{formatDate(row.date)}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        
                        {/* Pagination component */}
                        {reportData.pages > 1 && (
                            <div className="reports-healthy__pagination">
                                <CustomPaginate
                                    pageCount={reportData.pages}
                                    handlePageClick={handlePageClick}
                                    currentPage={currentPage}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Wrapper>
    );
};

export default ReportsHealthy;